import { Loading } from '@/components/shared/Loading';
import type { AdminRouterContext } from '@/context/AdminRouterContext';
import { ColorSchemeContext } from '@/context/ColorSchemeContext';
import { SitesRepository } from '@/repositories/SitesRepository';
import { FontSetContext } from '@stackje/shared-components/context/FontSetContext';
import { createFileRoute, getRouteApi, notFound, Outlet } from '@tanstack/react-router'
import { useContext, useEffect } from 'react';
import { useSiteQuery } from '@/hooks/useSiteQuery';
import { queryClient } from '@/lib/queryClient';
import { siteQueryKeys } from '@/hooks/useSiteQuery';
import { useSiteNotifications } from '@/hooks/useSiteNotifications';

export const Route = createFileRoute('/_protected/_site')({
  loader: async ({ context }: { context: AdminRouterContext }) => {
    const userId = context.auth.user?.id;
    
    // Try to get data from TanStack Query first 
    try {
      const cachedData = await queryClient.fetchQuery({
        queryKey: siteQueryKeys.site(userId!),
        queryFn: () => SitesRepository.getByUserId(),
      });
      
      if (cachedData.error) {
        throw notFound();
      }
      
      return cachedData.data;
    } catch {
      throw notFound();
    }
  },
  component: RouteComponent,
})
function RouteComponent() {
  const { setColorSchemeId, setCustomColors } = useContext(ColorSchemeContext)
  const { setFontSetId } = useContext(FontSetContext)
  const routeApi = getRouteApi("/_protected/_site")
  const data = routeApi.useLoaderData()
  
  // Use TanStack Query to keep data fresh in the background
  useSiteQuery(data?.userId)

  // Connect to real-time notifications for this site
  useSiteNotifications(data?.id)

  useEffect(() => {
    if (data?.siteDetails?.colorSchemeId) {
      setColorSchemeId(data.siteDetails.colorSchemeId);
    } else {
      setColorSchemeId("natural");
    }
    if (data?.siteDetails?.fontSetId) {
      setFontSetId(data.siteDetails.fontSetId);
    } else {
      setFontSetId("modern");
    }
    // Initialize custom colors from site data
    if (data?.siteDetails?.customColors) {
      setCustomColors(data.siteDetails.customColors);
    } else {
      setCustomColors(null);
    }
  }, [
    data?.siteDetails?.colorSchemeId,
    setColorSchemeId,
    data?.siteDetails?.fontSetId,
    setFontSetId,
    data?.siteDetails?.customColors,
    setCustomColors,
  ]);

  if (!data) {
    return <Loading />
  }

  return <Outlet />
}
